// @givmo/auth — TOTP second-factor challenge (AS-WEB1).
//
// GCIP raises `auth/multi-factor-auth-required` when the FIRST factor (password or OAuth) succeeded
// but the account carries an enrolled second factor. The Web SDK hands back a `MultiFactorResolver`
// holding a short-lived pending credential; the sign-in only completes once `resolveSignIn` is
// called with an assertion built from a code the user reads off their authenticator app. Consumers
// enrol on mobile — the web portals ship no enrolment and only ever have to COMPLETE a challenge,
// which they must, because an admin who enrolled on their phone otherwise cannot get in at all.
//
// WHY the challenge is modelled as a CONTINUATION carried on the thrown error, rather than a boolean
// the caller re-derives: the challenge has to be a real state of each app's sign-in machine, and the
// flow that was interrupted still owes its own tail (for the admin flows, the company +
// email-verified routing decision). Threading `finish` through the error means a screen catches,
// prompts for a code, and resumes on EXACTLY the path the happy case would have taken — instead of a
// second, drifting copy of the post-credential logic living in the UI.
//
// TOTP codes are SINGLE-USE per 30-second window (verified against the live dev GCIP project,
// 2026-07-26): a code already spent is rejected and only the next window's code succeeds. So
// `resolve()` spends one code per call and must never be re-invoked with the same one — callers
// clear their input on rejection and ask for the current code.
//
// Phone is deliberately unhandled. Givmo never enrols SMS as a second factor (phone-OTP sign-in is
// single-factor possession), so a resolver carrying no TOTP hint surfaces as an honest
// "can't be completed here" error rather than a prompt no authenticator can satisfy.

import {
  getMultiFactorResolver,
  TotpMultiFactorGenerator,
  type Auth,
  type MultiFactorError,
  type MultiFactorResolver,
  type UserCredential,
} from 'firebase/auth';
import { AuthError, SECOND_FACTOR_REQUIRED_CODE } from './errors';

/** Shown when GCIP asks for a factor kind the web cannot complete (i.e. anything but TOTP). */
const UNSUPPORTED_SECOND_FACTOR_MESSAGE =
  'Your account requires a second factor that cannot be completed here. Please sign in from the Givmo app.';

/** Shown when a code already spent against this challenge is submitted again. */
const CODE_ALREADY_SPENT_MESSAGE =
  'You already used that code. Wait for your authenticator app to show the next one.';

/**
 * A pending TOTP second-factor challenge: everything a screen needs to finish an interrupted
 * sign-in, and nothing it does not. The resolver and its pending credential stay closed over inside
 * `resolve` so no caller can log or serialise them.
 */
export interface TotpChallenge<T = UserCredential> {
  /**
   * The enrolled factor's display name when the user set one at enrolment, else `null`. Display
   * only — never the factor's uid and never the shared secret.
   */
  readonly factorDisplayName: string | null;
  /**
   * Complete the challenge with a code read off the authenticator app RIGHT NOW, resolving to the
   * originating flow's success value. Each call SPENDS that code: resubmitting one this challenge
   * has already seen is refused here without reaching Firebase, so the caller must collect a fresh
   * one. Rejects with an {@link AuthError} — `invalidVerificationCode` for a wrong, expired or
   * already-spent code, `sessionExpired` once the pending credential itself is gone (which needs a
   * fresh sign-in, not another code).
   */
  resolve(code: string): Promise<T>;
}

/**
 * The `AuthError` a sign-in throws when the first factor succeeded but a TOTP second factor is
 * outstanding. It stays an `AuthError` of kind `secondFactorRequired`, so an app with no challenge
 * UI still shows honest copy instead of "Something went wrong", while an app that HAS one reads
 * {@link secondFactorChallengeOf} and drives the challenge.
 */
export class SecondFactorRequiredError<T = UserCredential> extends AuthError {
  readonly challenge: TotpChallenge<T>;

  constructor(challenge: TotpChallenge<T>) {
    super('secondFactorRequired', { code: SECOND_FACTOR_REQUIRED_CODE });
    this.name = 'SecondFactorRequiredError';
    this.challenge = challenge;
    // Same prototype-chain restoration as `AuthError` — `instanceof` must survive down-levelling.
    Object.setPrototypeOf(this, SecondFactorRequiredError.prototype);
  }
}

/**
 * Normalise a caught sign-in failure, upgrading a pending TOTP challenge into a
 * {@link SecondFactorRequiredError} that carries the resolver. Every other failure falls through to
 * `AuthError.from`, so this is a drop-in replacement at any sign-in throw site.
 *
 * `finish` maps the credential the resolver produces onto the flow's own success value, running the
 * SAME tail the happy path runs; omit it when the flow's success value IS the credential. It is
 * called only after the code is accepted, so a rejected code never advances the flow.
 *
 * Accepts all three shapes a challenge arrives in, which is what lets one helper serve every layer:
 * the raw Firebase `MultiFactorError`; the `AuthError` an inner layer already normalised (whose
 * source `AuthError.from` retained for exactly this); and a `SecondFactorRequiredError` from an
 * inner primitive, whose challenge is re-targeted at this layer's success value rather than rebuilt
 * — a resolver may be consumed only once, so rebuilding it would be wrong as well as wasteful.
 */
export function withSecondFactorChallenge<T = UserCredential>(
  auth: Auth,
  error: unknown,
  finish?: (credential: UserCredential) => Promise<T>,
): AuthError {
  // Identity when the caller's success value is the credential; the cast is sound because `finish`
  // is omitted only when `T` was left at its `UserCredential` default.
  const complete =
    finish ?? ((credential: UserCredential) => Promise.resolve(credential as unknown as T));

  // An inner primitive (`signInWithStrategy`) already built the challenge — it resolves to a bare
  // credential, so wrap its `resolve` with this layer's tail instead of touching the resolver again.
  if (error instanceof SecondFactorRequiredError) {
    const inner = error.challenge;
    return new SecondFactorRequiredError<T>({
      factorDisplayName: inner.factorDisplayName,
      resolve: async (code) => complete(await inner.resolve(code)),
    });
  }

  const source = secondFactorSourceOf(error);
  if (source === null) {
    return AuthError.from(error);
  }

  // `getMultiFactorResolver` asserts on the error's internal MFA data, so it throws — not returns —
  // if anything else ever carries this code. Since this runs INSIDE a caller's `catch`, letting that
  // escape would replace a handled sign-in failure with an unhandled crash. Degrade to the plain
  // error instead: the user still gets the honest "two-factor required" copy.
  let resolver: MultiFactorResolver;
  try {
    resolver = getMultiFactorResolver(auth, source);
  } catch {
    return AuthError.from(error);
  }

  const totpHint = resolver.hints.find(
    (hint) => hint.factorId === TotpMultiFactorGenerator.FACTOR_ID,
  );
  if (totpHint === undefined) {
    return new AuthError('secondFactorRequired', {
      code: SECOND_FACTOR_REQUIRED_CODE,
      message: UNSUPPORTED_SECOND_FACTOR_MESSAGE,
    });
  }

  // Codes already spent against THIS challenge. Enforcing single use here — rather than trusting
  // each screen's own guard — makes it an invariant of the challenge itself: five apps now render a
  // prompt, and a resubmitted code must be impossible in all of them, including any app wired later
  // that forgets the UI affordance. Screens still track their own set, but only to disable the
  // button and explain why; this is the guarantee.
  const spentCodes = new Set<string>();

  return new SecondFactorRequiredError<T>({
    factorDisplayName: totpHint.displayName ?? null,
    resolve: async (code) => {
      if (spentCodes.has(code)) {
        throw new AuthError('invalidVerificationCode', { message: CODE_ALREADY_SPENT_MESSAGE });
      }
      spentCodes.add(code);

      let credential: UserCredential;
      try {
        const assertion = TotpMultiFactorGenerator.assertionForSignIn(totpHint.uid, code);
        credential = await resolver.resolveSignIn(assertion);
      } catch (rejected) {
        throw AuthError.from(rejected);
      }
      return complete(credential);
    },
  });
}

/**
 * The pending challenge a caught error carries, or `null` when it carries none. `T` is the success
 * value of the flow the CALLER invoked; since a challenge is only ever built by the flow that throws
 * it, the caller already knows that type and this asserts rather than proves it.
 */
export function secondFactorChallengeOf<T = UserCredential>(
  error: unknown,
): TotpChallenge<T> | null {
  return error instanceof SecondFactorRequiredError ? (error.challenge as TotpChallenge<T>) : null;
}

/**
 * The raw Firebase `MultiFactorError` behind a caught value, or `null` when the failure is not a
 * pending second-factor challenge. Reads through an `AuthError` to the source it retained, because
 * the resolver can only be built from the original error object.
 */
function secondFactorSourceOf(error: unknown): MultiFactorError | null {
  const raw = error instanceof AuthError ? AuthError.sourceOf(error) : error;
  if (typeof raw !== 'object' || raw === null) return null;
  const code = (raw as { code?: unknown }).code;
  return code === SECOND_FACTOR_REQUIRED_CODE ? (raw as MultiFactorError) : null;
}
